import SectionVisible from 'appContainer/SectionVisible';
import { ReactComponent as ShowClaim } from 'assett/icons/show_password.svg';
import { useClaimHook } from 'hooks/claim.hook';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store/store';
import {
	Button,
	CompositeTypography,
	MarkdownRenderer,
	Typography,
} from 'styles';
import Paper from 'styles/paper/paper';
import Stack from 'styles/stack/Stack';
import { SECTIONS } from 'types/SectionsVisibility';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { formatMarkdown } from 'utils/utils';
import ClaimStatusBreadcrumbs from './ClaimStatusBreadcrumbs.component';
import ClaimReturnDetails from './components/ClaimReturn/ClaimReturnDetails.component';

const ClaimMemberDetails = () => {
	const navigate = useNavigate();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.DB_LABELS,
	});

	const { claimData: claim } = useSelector((store: RootState) => store.claim);
	const { fraudDetail } = useSelector((store: RootState) => store.fraudTracking);
	const { complaintDetail } = useSelector(
		(store: RootState) => store.complaintTracking,
	);
	const { complaintStatuses } = useSelector((store: RootState) => store.form);
	const {
		showInternalNote,
		showPendingNote,
		showRejectedNote,
		showReturnStatus,
	} = useClaimHook();

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2.5,
				justifyContent: 'space-between',
				display: 'flex',
				borderRadius: 0.5,
				width: '100%',
			}}
		>
			<Stack flexGrow={1} rowGap={1.875}>
				<CompositeTypography
					label={tClientLabels('claim.labels.memberFullName')}
					value={claim?.beneficiaryFullName as string}
				/>
				<CompositeTypography
					label={tClientLabels('claim.labels.memberId')}
					value={claim?.beneficiaryMemberId as string}
				/>
				<CompositeTypography
					label={tClientLabels('claim.labels.memberDesignation')}
					value={tDbLabels(claim?.memberDesignationLabel as string)}
				/>
				{claim?.memberDesignation === 'Dependant' && (
					<CompositeTypography
						label={tClientLabels('claim.labels.subscriberName')}
						value={tDbLabels(claim?.memberFullName as string)}
					/>
				)}
				{claim?.claimCarrierIntegrationInfo.carrierClaimId && (
					<CompositeTypography
						label={tClientLabels('claim.labels.carrierClaimId')}
						value={tDbLabels(
							claim?.claimCarrierIntegrationInfo.carrierClaimId as string,
						)}
					/>
				)}
			</Stack>
			<Stack flexGrow={0} width={'50%'} rowGap={1.875}>
				<CompositeTypography
					label={tClientLabels('claim.labels.planName')}
					value={claim?.planName as string}
				/>
				<CompositeTypography
					label={tClientLabels('claim.labels.memberProviderLocation')}
					value={claim?.memberProviderLocation as string}
				/>
				<CompositeTypography
					label={tClientLabels('claim.labels.status')}
					value={claim?.status.label as string}
					children={<ClaimStatusBreadcrumbs claim={claim} />}
				/>
				{showInternalNote && (
					<CompositeTypography
						label={tClientLabels('claim.labels.note')}
						value={claim?.status.internalNote as string}
						notBold
						children={
							<Typography
								variant={'body'}
								sx={{
									'& > p': { padding: 0, margin: 0 },
									'& > ul': { margin: 0, paddingLeft: '20px' },
									'& > ul > li > p': { padding: 0, margin: 0 },
								}}
							>
								<MarkdownRenderer>
									{formatMarkdown(claim?.status.internalNote as string)}
								</MarkdownRenderer>
							</Typography>
						}
					/>
				)}
				{(showPendingNote || showRejectedNote) && (
					<>
						<SectionVisible
							sectionName={SECTIONS.ENABLE_CLAIM_REJECTION_DEFAULT_FIELDS}
							carrier={{ customLayout: claim.customLayout }}
						>
							<CompositeTypography
								label={tClientLabels('claim.labels.argument')}
								value={claim?.status.internalNote as string}
								notBold
							/>
						</SectionVisible>
						<SectionVisible
							sectionName={SECTIONS.ENABLE_CLAIM_REJECTION_ADDITIONAL_FIELDS}
							carrier={{ customLayout: claim.customLayout }}
						>
							<CompositeTypography
								label={
									showRejectedNote
										? tClientLabels('claim.labels.denialReason')
										: tClientLabels('claim.labels.argument')
								}
								value={claim?.status.internalNote as string}
								notBold
							/>
						</SectionVisible>
					</>
				)}

				{complaintDetail?.complaintId ? (
					<CompositeTypography
						label={tClientLabels('claim.labels.complaintStatus')}
						value=""
						children={
							<Stack direction="row" justifyContent="space-between" width="100%">
								<Typography sx={{ ml: 1 }} variant={'bodyLink'}>
									{tDbLabels(
										complaintStatuses.find(c => c.code === complaintDetail?.status)
											?.label || '-',
									)}
								</Typography>
								<Stack direction="row" gap="30px">
									<Button
										startIcon={<ShowClaim height="13px" />}
										variant="text"
										onClick={() => {
											navigate(
												`/view-claims/${claim?.claimUUID}/view-complaint/${complaintDetail?.complaintId}`,
											);
										}}
										sx={{
											textDecoration: 'underline',
											'&:hover': {
												textDecoration: 'underline',
											},
											p: 0,
										}}
									>
										{tClientLabels('commons.buttons.viewComplaint')}
									</Button>
								</Stack>
							</Stack>
						}
					/>
				) : null}
				{fraudDetail?.fraudId ? (
					<CompositeTypography
						label={tClientLabels('claim.labels.fraud')}
						value=""
						children={
							<Stack direction="row" justifyContent="space-between" width="100%">
								<Typography sx={{ ml: 1 }} variant={'bodyLink'}>
									{fraudDetail?.investigationStatus
										? tDbLabels(
												`fraud_status.${fraudDetail?.investigationStatus?.toLowerCase()}`,
											)
										: '-'}
								</Typography>
								<Stack direction="row" gap="30px">
									<Button
										startIcon={<ShowClaim height="13px" />}
										variant="text"
										onClick={() => {
											navigate(
												`/view-claims/${claim?.claimUUID}/view-fraud/${fraudDetail?.fraudId}`,
											);
										}}
										sx={{
											textDecoration: 'underline',
											'&:hover': {
												textDecoration: 'underline',
											},
											p: 0,
										}}
									>
										{tClientLabels('commons.buttons.viewFraud')}
									</Button>
								</Stack>
							</Stack>
						}
					/>
				) : null}
				{showReturnStatus && <ClaimReturnDetails />}
			</Stack>
		</Paper>
	);
};

export default ClaimMemberDetails;
